import React, { useState, useMemo } from 'react'
import { Typography, Flex, Button, Popover, Avatar, Spin } from 'antd'
import { CloseOutlined, LoadingOutlined } from '@ant-design/icons'
import { Location20Regular, SignOut20Regular } from '@fluentui/react-icons'

import { getAbbreviation } from '../constants/helpers/common'
import { ICurrentUser } from '../constants/types/common'
import { NamespaceSelector } from './NamespaceSelector'

const { Text } = Typography

export interface IPermissionManagerHeaderProps {
  user: ICurrentUser | null
  logout: () => void
}

export const PermissionManagerHeader = ({ user, logout }: IPermissionManagerHeaderProps) => {
  const [open, setOpen] = useState(false)
  const { given_name, family_name, email, zoneinfo = '' } = user || {}

  const abbr: string = useMemo(() => {
    if (!given_name || !family_name) return ''
    return getAbbreviation(given_name, family_name)
  }, [given_name, family_name])

  return (
    <>
      <NamespaceSelector />
      {user
        ? (
            <Popover
              content={(
                <PopoverContent
                  given_name={given_name}
                  family_name={family_name}
                  email={email}
                  zoneinfo={zoneinfo}
                  abbr={abbr}
                  hide={() => setOpen(false)}
                  logout={logout}
                />
              )}
              placement='bottomRight'
              trigger='click'
              open={open}
              onOpenChange={setOpen}
            >
              <Avatar
                children={abbr}
                className='avatar__icon'
                data-testid='user-avatar'
              />
            </Popover>
          )
        : (
            <Spin indicator={<LoadingOutlined spin={true} style={{ fontSize: 32 }} />} />
          )}
    </>
  )
}
export default PermissionManagerHeader

const PopoverContent = ({ given_name, family_name, email, zoneinfo, abbr, hide, logout }) => (
  <div
    className='wpp-header__user-popover'
    data-testid='user-popover'
  >
    <Flex
      gap={8}
      justify='flex-start'
      align='center'
    >
      <Avatar
        size={72}
        className='avatar__large'>
        {abbr}
      </Avatar>

      <div style={{ flexGrow: 2 }}>
        {(given_name || family_name) && (
          <Text className='wpp-header__user-popover-name'>
            {given_name} {family_name}
          </Text>
        )}
        <Text>{email}</Text>
        <div className='wpp-header__user-popover-location'>
          <Location20Regular className='wpp-header__user-popover-location-icon' />
          <Text>{zoneinfo}</Text>
        </div>
      </div>
      <Button
        icon={<CloseOutlined />}
        className='wpp-header__user-popover-close'
        onClick={hide}
        color='default'
        variant='link'
      />
    </Flex>

    <hr className='wpp-header__user-popover-delimiter' />

    <Button
      aria-label='Logout'
      color='default'
      variant='text'
      onClick={logout}
    >
      <SignOut20Regular className='wpp-header__user-popover-logout-icon' />
      Logout
    </Button>
  </div>
)
